// Sistema de Alertas Automáticos por Email
// Envia notificações imediatas sobre leads e atividades importantes

export interface AlertData {
  tipo: 'novo_lead' | 'formulario_contato' | 'chat_assistant' | 'whatsapp_click' | 'recuperacao_sinistro' | 'analise_solicitada';
  timestamp: string;
  dadosLead: {
    nome?: string;
    email?: string;
    telefone?: string;
    empresa?: string;
    interesse?: string;
    mensagem?: string;
    origem: string;
    pagina: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  };
  prioridade: 'alta' | 'media' | 'baixa';
  valorEstimado?: string;
}

// Configurações de alertas
const ALERT_CONFIG = {
  // Tipos de alerta que disparam emails imediatamente
  alertasImediatos: [
    'novo_lead',
    'formulario_contato', 
    'chat_assistant',
    'recuperacao_sinistro',
    'analise_solicitada'
  ],
  
  // Limiar de prioridade para alertas urgentes
  prioridadeUrgente: ['alta'],
  
  // Email endpoint do Readdy para alertas
  emailEndpoint: 'https://readdy.ai/api/form/d6kaqpag895l60t54340'
};

// Determina prioridade baseada no tipo e dados
function determinarPrioridade(tipo: AlertData['tipo'], dadosLead: AlertData['dadosLead']): AlertData['prioridade'] {
  // Alta prioridade
  if (tipo === 'recuperacao_sinistro') return 'alta';
  if (tipo === 'chat_assistant' && dadosLead.interesse) return 'alta';
  if (dadosLead.empresa && dadosLead.empresa.trim() !== '') return 'alta';
  
  // Média prioridade  
  if (tipo === 'formulario_contato') return 'media';
  if (tipo === 'analise_solicitada') return 'media';
  
  // Baixa prioridade
  return 'baixa';
}

// Gera estimativa de valor do lead
function estimarValorLead(dadosLead: AlertData['dadosLead']): string {
  if (dadosLead.interesse?.toLowerCase().includes('empresarial')) return 'R$ 15.000 - R$ 50.000/ano';
  if (dadosLead.interesse?.toLowerCase().includes('recuperação')) return 'R$ 50.000 - R$ 200.000';
  if (dadosLead.interesse?.toLowerCase().includes('gestão')) return 'R$ 10.000 - R$ 30.000/ano';
  if (dadosLead.interesse?.toLowerCase().includes('automóvel')) return 'R$ 2.000 - R$ 8.000/ano';
  if (dadosLead.empresa) return 'R$ 8.000 - R$ 25.000/ano';
  return 'R$ 1.500 - R$ 5.000/ano';
}

// Formata dados do lead para email
function formatarDadosParaEmail(alertData: AlertData): string {
  const { dadosLead, tipo, prioridade, valorEstimado } = alertData;
  
  const prioridadeIcon = {
    'alta': '🔴 ALTA PRIORIDADE',
    'media': '🟡 MÉDIA PRIORIDADE', 
    'baixa': '🟢 BAIXA PRIORIDADE'
  };

  const tipoIcon = {
    'novo_lead': '👤 NOVO LEAD',
    'formulario_contato': '📝 FORMULÁRIO CONTATO',
    'chat_assistant': '💬 CHAT ASSISTANT',
    'whatsapp_click': '📱 CLIQUE WHATSAPP',
    'recuperacao_sinistro': '🆘 RECUPERAÇÃO SINISTRO',
    'analise_solicitada': '📊 ANÁLISE SOLICITADA'
  };

  let emailBody = `${prioridadeIcon[prioridade]} - ${tipoIcon[tipo]}

═══════════════════════════════════
📊 DETALHES DO LEAD
═══════════════════════════════════

👤 NOME: ${dadosLead.nome || 'Não informado'}
📧 EMAIL: ${dadosLead.email || 'Não informado'}  
📱 TELEFONE: ${dadosLead.telefone || 'Não informado'}
🏢 EMPRESA: ${dadosLead.empresa || 'Não informada'}
🎯 INTERESSE: ${dadosLead.interesse || 'Não especificado'}

═══════════════════════════════════
🌐 INFORMAÇÕES DE ORIGEM  
═══════════════════════════════════

📍 ORIGEM: ${dadosLead.origem}
📄 PÁGINA: ${dadosLead.pagina}`;

  if (dadosLead.utmSource || dadosLead.utmMedium || dadosLead.utmCampaign) {
    emailBody += `
📈 UTM SOURCE: ${dadosLead.utmSource || 'N/A'}
📊 UTM MEDIUM: ${dadosLead.utmMedium || 'N/A'}  
🎯 UTM CAMPAIGN: ${dadosLead.utmCampaign || 'N/A'}`;
  }

  if (dadosLead.mensagem) {
    emailBody += `

═══════════════════════════════════
💬 MENSAGEM DO CLIENTE
═══════════════════════════════════

${dadosLead.mensagem}`;
  }

  emailBody += `

═══════════════════════════════════
💰 ANÁLISE COMERCIAL
═══════════════════════════════════

💵 VALOR ESTIMADO: ${valorEstimado}
⏰ RECEBIDO EM: ${new Date(alertData.timestamp).toLocaleString('pt-BR')}
🚨 PRIORIDADE: ${prioridade.toUpperCase()}

═══════════════════════════════════
🎯 PRÓXIMOS PASSOS SUGERIDOS
═══════════════════════════════════`;

  if (prioridade === 'alta') {
    emailBody += `

🔥 AÇÃO URGENTE NECESSÁRIA:
• Entrar em contato em até 1 hora
• Priorizar este lead na agenda
• Preparar proposta personalizada
• Acompanhar de perto`;
  } else if (prioridade === 'media') {
    emailBody += `

⚡ AÇÃO RECOMENDADA:
• Entrar em contato em até 4 horas  
• Fazer análise preliminar
• Enviar material relevante
• Agendar reunião`;
  } else {
    emailBody += `

📞 AÇÃO PADRÃO:
• Entrar em contato em até 24 horas
• Enviar informações gerais
• Incluir no funil de nutrição
• Acompanhar evolução`;
  }

  if (dadosLead.telefone) {
    emailBody += `

📱 LINK WHATSAPP DIRETO:
https://wa.me/55${dadosLead.telefone.replace(/\D/g, '')}`;
  }

  emailBody += `

═══════════════════════════════════
🏢 IBX BRASIL - SISTEMA DE ALERTAS
═══════════════════════════════════`;

  return emailBody;
}

// Envia alerta por email
export async function enviarAlertaEmail(alertData: AlertData): Promise<boolean> {
  try {
    // Verifica se deve enviar alerta
    if (!ALERT_CONFIG.alertasImediatos.includes(alertData.tipo)) {
      console.log(`Tipo ${alertData.tipo} não configurado para alertas automáticos`);
      return false;
    }

    // Prepara dados do formulário
    const emailContent = formatarDadosParaEmail(alertData);
    
    const formData = new URLSearchParams();
    formData.append('tipo_alerta', alertData.tipo);
    formData.append('prioridade', alertData.prioridade);
    formData.append('nome_lead', alertData.dadosLead.nome || 'Não informado');
    formData.append('email_lead', alertData.dadosLead.email || 'Não informado');
    formData.append('telefone_lead', alertData.dadosLead.telefone || 'Não informado');
    formData.append('empresa_lead', alertData.dadosLead.empresa || 'Não informada');
    formData.append('interesse_lead', alertData.dadosLead.interesse || 'Não especificado');
    formData.append('origem_lead', alertData.dadosLead.origem);
    formData.append('pagina_origem', alertData.dadosLead.pagina);
    formData.append('valor_estimado', alertData.valorEstimado || 'Não calculado');
    formData.append('timestamp', alertData.timestamp);
    formData.append('detalhes_completos', emailContent);
    formData.append('sistema', 'IBX Brasil - Alertas Automáticos');

    // Envia para o endpoint
    const response = await fetch(ALERT_CONFIG.emailEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString()
    });

    if (response.ok) {
      console.log(`✅ Alerta ${alertData.tipo} enviado com sucesso`);
      return true;
    } else {
      console.error(`❌ Erro ao enviar alerta: ${response.status}`);
      return false;
    }

  } catch (error) {
    console.error('❌ Erro ao processar alerta:', error);
    return false;
  }
}

// Função principal para registrar e enviar alertas
export async function registrarAlerta(
  tipo: AlertData['tipo'],
  dadosLead: Partial<AlertData['dadosLead']>,
  mensagemAdicional?: string
): Promise<void> {
  try {
    // Captura dados da página atual
    const dadosCompletos: AlertData['dadosLead'] = {
      nome: dadosLead.nome,
      email: dadosLead.email,
      telefone: dadosLead.telefone,
      empresa: dadosLead.empresa,
      interesse: dadosLead.interesse,
      mensagem: dadosLead.mensagem || mensagemAdicional,
      origem: dadosLead.origem || 'Site IBX Brasil',
      pagina: window.location.pathname,
      utmSource: dadosLead.utmSource || new URLSearchParams(window.location.search).get('utm_source') || undefined,
      utmMedium: dadosLead.utmMedium || new URLSearchParams(window.location.search).get('utm_medium') || undefined,
      utmCampaign: dadosLead.utmCampaign || new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
    };

    // Monta dados do alerta
    const prioridade = determinarPrioridade(tipo, dadosCompletos);
    const valorEstimado = estimarValorLead(dadosCompletos);
    
    const alertData: AlertData = {
      tipo,
      timestamp: new Date().toISOString(),
      dadosLead: dadosCompletos,
      prioridade,
      valorEstimado
    };

    // Envia alerta
    await enviarAlertaEmail(alertData);

    // Salva no storage local para backup
    const alertas = JSON.parse(localStorage.getItem('ibx_alertas_enviados') || '[]');
    alertas.push(alertData);
    
    // Mantém apenas os últimos 50 alertas
    if (alertas.length > 50) {
      alertas.splice(0, alertas.length - 50);
    }
    
    localStorage.setItem('ibx_alertas_enviados', JSON.stringify(alertas));

    console.log(`🔔 Alerta registrado: ${tipo} (${prioridade})`);

  } catch (error) {
    console.error('❌ Erro ao registrar alerta:', error);
  }
}

// Função para alertas de WhatsApp
export async function alertarClickWhatsApp(
  servicoInteresse: string,
  paginaOrigem: string,
  textoMensagem?: string
): Promise<void> {
  await registrarAlerta('whatsapp_click', {
    interesse: servicoInteresse,
    origem: 'Clique WhatsApp',
    pagina: paginaOrigem,
    mensagem: textoMensagem || `Interesse demonstrado via WhatsApp para: ${servicoInteresse}`
  });
}

// Função para alertas de formulário
export async function alertarSubmissaoFormulario(
  formData: {
    nome: string;
    email: string;
    telefone?: string;
    empresa?: string;
    interesse?: string;
    mensagem?: string;
  },
  origemFormulario: string
): Promise<void> {
  await registrarAlerta('formulario_contato', {
    nome: formData.nome,
    email: formData.email,
    telefone: formData.telefone,
    empresa: formData.empresa,
    interesse: formData.interesse,
    mensagem: formData.mensagem,
    origem: origemFormulario
  });
}

// Função para alertas do chat assistant  
export async function alertarChatAssistant(
  formData: {
    name: string;
    email: string;
    phone: string;
    interest?: string;
    message?: string;
  }
): Promise<void> {
  await registrarAlerta('chat_assistant', {
    nome: formData.name,
    email: formData.email,
    telefone: formData.phone,
    interesse: formData.interest,
    mensagem: formData.message,
    origem: 'Chat Assistant'
  });
}

// Função para solicitações de análise gratuita
export async function alertarAnaliseGratuita(
  dadosLead: {
    nome: string;
    email: string;
    telefone?: string;
    empresa?: string;
    interesse?: string;
  },
  origem: string = 'Solicitação Análise'
): Promise<void> {
  await registrarAlerta('analise_solicitada', {
    ...dadosLead,
    origem,
    mensagem: 'Solicitação de análise gratuita de seguros'
  });
}

// Função para casos de recuperação de sinistros
export async function alertarRecuperacaoSinistro(
  dadosLead: {
    nome: string;
    email: string;
    telefone?: string;
    empresa?: string;
    mensagem?: string;
  },
  valorSinistro?: string
): Promise<void> {
  await registrarAlerta('recuperacao_sinistro', {
    ...dadosLead,
    interesse: 'Recuperação de Sinistros',
    origem: 'Solicitação Recuperação Sinistro',
    mensagem: `${dadosLead.mensagem || ''} ${valorSinistro ? `| Valor estimado: ${valorSinistro}` : ''}`.trim()
  });
}

// Função para obter histórico de alertas (para painel admin)
export function obterHistoricoAlertas(limite: number = 20): AlertData[] {
  try {
    const alertas = JSON.parse(localStorage.getItem('ibx_alertas_enviados') || '[]');
    return alertas.slice(-limite).reverse(); // Retorna os mais recentes primeiro
  } catch (error) {
    console.error('Erro ao obter histórico de alertas:', error);
    return [];
  }
}

// Função para limpar alertas antigos  
export function limparAlertasAntigos(diasParaManter: number = 30): void {
  try {
    const alertas = JSON.parse(localStorage.getItem('ibx_alertas_enviados') || '[]');
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - diasParaManter);

    const alertasFiltrados = alertas.filter((alerta: AlertData) => {
      return new Date(alerta.timestamp) >= cutoffDate;
    });

    localStorage.setItem('ibx_alertas_enviados', JSON.stringify(alertasFiltrados));
    console.log(`🧹 Alertas antigos removidos. Mantidos: ${alertasFiltrados.length}`);
  } catch (error) {
    console.error('Erro ao limpar alertas antigos:', error);
  }
}